// Config-completeness status for one building — powers the four "Konfiguraation tila" status
// cards that double as the Konfiguraatio tab bar. Everything is derived from configuration
// (no live values).
//
// This module only fetches; the card derivation is pure and lives in config-status-dimensions.ts.
// Completeness itself comes from the canonical KB endpoint `GET /buildings/{id}/completeness` —
// the same computation whose portfolio sibling (`/buildings/completeness-summary`) feeds the
// Konfiguraatio portfolio view — so a building's own page and the portfolio can never disagree
// about it (docs/spec/concepts.md § Configuration completeness).
import { useEffect, useState } from "react";

import {
  listBuildingServices,
  listEquipmentDependencies,
  type BuildingService,
} from "@/lib/api/building";
import {
  getBuildingCompleteness,
  type BuildingCompleteness,
} from "@/lib/api/agent";
import {
  deriveDimensions,
  type ConfigDimension,
} from "@/lib/config-status-dimensions";

export type {
  ConfigDimension,
  ConfigTabKey,
  ConfigTone,
} from "@/lib/config-status-dimensions";

export interface BuildingConfigStatus {
  loading: boolean;
  dimensions: ConfigDimension[];
  // The canonical rollup + per-equipment breakdown, passed through untouched so the caller can
  // render the same numbers the portfolio shows. Null while loading, or if the endpoint failed.
  completeness: BuildingCompleteness | null;
}

interface RawStatus {
  completeness: BuildingCompleteness | null;
  services: BuildingService[];
  dependencyCount: number;
}

async function loadRawStatus(buildingId: string): Promise<RawStatus> {
  const [completeness, services, dependencies] = await Promise.all([
    getBuildingCompleteness(buildingId).catch(() => null),
    listBuildingServices(buildingId).catch((): BuildingService[] => []),
    listEquipmentDependencies(buildingId).catch(() => []),
  ]);
  return { completeness, services, dependencyCount: dependencies.length };
}

// refreshKey forces a re-fetch (e.g. after wiring a signal or toggling a service) so the
// status cards reflect the change without a full page reload.
export function useBuildingConfigStatus(
  buildingId: string,
  refreshKey = 0,
): BuildingConfigStatus {
  const [dimensions, setDimensions] = useState<ConfigDimension[]>([]);
  const [completeness, setCompleteness] = useState<BuildingCompleteness | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadRawStatus(buildingId).then((raw) => {
      if (cancelled) return;
      setDimensions(
        deriveDimensions(raw.completeness, raw.services, raw.dependencyCount),
      );
      setCompleteness(raw.completeness);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [buildingId, refreshKey]);

  return { loading, dimensions, completeness };
}
